import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { requestAccess } from '../../services/access-request/index.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import { cjnEligibilityCacheHandler, useSnooze } from '../../services/caching/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useNudgeStateJiraToJpd } from '../../services/nudge-state/index.tsx';
import { useRequestAccessErrorFlag } from '../request-access-error-flag/index.tsx';
import {
	getAnalyticsAttributes,
	getJPDCJNMessageStrings,
	useRequestAccessPromptFlag,
} from '../request-access-prompt-flag/index.tsx';
import { useRequestAccessSuccessFlag } from '../request-access-success-flag/index.tsx';
import { MAX_FEATURE_VISIT_COUNT } from './common.tsx';

const MAX_NUMBER_OF_JPD_COLLABORATORS = 10;

export const useJpdCrossJoinNudge = () => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'jira-product-discovery',
		MAX_NUMBER_OF_JPD_COLLABORATORS,
	);
	const showRequestAccessPromptFlag = useRequestAccessPromptFlag();
	const showSuccessFlag = useRequestAccessSuccessFlag();
	const showErrorFlag = useRequestAccessErrorFlag();
	const { push } = useRouterActions();
	const { snoozeFor30Days, getVisitedCount, increaseVisitedCount } = useSnooze(
		'crossJoinNudgesJiraToJpd',
	);
	const { getNudgeState, setNudgeDismissed } = useNudgeStateJiraToJpd();

	return useCallback(async () => {
		try {
			const startTimestamp = Date.now();
			const collaboratorsOnJpd = await getFilteredCollaborators();
			const onSubmit = async () => {
				try {
					const accessStatus = await requestAccess(
						'jira-product-discovery',
						cloudId,
						userId,
						'crossJoinNudgesJiraToJpd',
						createAnalyticsEvent,
					);
					if (
						accessStatus === 'ACCESS_GRANTED' ||
						accessStatus === 'ACCESS_EXISTS' ||
						accessStatus === 'APPROVED_REQUEST_EXISTS'
					) {
						push('/jira/projects');
					} else if (
						accessStatus === 'PENDING_REQUEST_CREATED' ||
						accessStatus === 'PENDING_REQUEST_EXISTS'
					) {
						showSuccessFlag();
					} else {
						showErrorFlag('jira-product-discovery');
					}
				} catch {
					showErrorFlag('jira-product-discovery');
				}
			};

			const isUserOptOut = (await getNudgeState()) === 'DISMISSED';
			const featureVisitedCount = await getVisitedCount();
			if (isUserOptOut || featureVisitedCount >= MAX_FEATURE_VISIT_COUNT) return;

			showRequestAccessPromptFlag({
				users: collaboratorsOnJpd,
				onSubmit,
				onOptout: setNudgeDismissed,
				snoozeFor30Days,
				integration: 'crossJoinNudgesJiraToJpd',
				featureVisitedCount,
				messageParser: getJPDCJNMessageStrings,
				eventAttributes: getAnalyticsAttributes({
					userId,
					users: collaboratorsOnJpd,
					featureVisitedCount,
					startTimestamp,
				}),
			});

			await Promise.all([
				increaseVisitedCount(featureVisitedCount + 1),
				cjnEligibilityCacheHandler.set('has_seen_jpd', { value: true }),
			]);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('crossJoinNudgesJiraToJpd', error, { cloudId });
		}
	}, [
		cloudId,
		createAnalyticsEvent,
		getFilteredCollaborators,
		getNudgeState,
		getVisitedCount,
		increaseVisitedCount,
		push,
		setNudgeDismissed,
		showErrorFlag,
		showRequestAccessPromptFlag,
		showSuccessFlag,
		snoozeFor30Days,
		userId,
	]);
};
